/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { hooks, unstable } from '@broadleaf/admin-components';

const { useTracking } = hooks;
const { ContextRequest } = unstable;

const { ContextParameters } = ContextRequest;

/**
 * @typedef {Object} OrderFulfillmentContextParams
 * @property {Object} tracking - tracking parameters
 * @property {string|null} id - the order fulfillment ID
 * @property {string|null} orderId - the order ID
 * @property {OrderFulfillment} parent - alias for the order fulfillment data
 * @property {OrderFulfillment} fulfillment - the order fulfillment data
 * @property {Order} order - the order data
 */

/**
 * Returns the context parameters for the current order fulfillment card.
 *
 * <p>
 * If the `state` is provided, this will include the `state` as a context parameter,
 * as well as place the order fulfillment data at the `order`. This allows downstream
 * actions and other components to more easily reference order properties in
 * their urls and parameters.
 *
 * @param {Object} metadata the order metadata
 * @param {OrderLocalState} [orderState] the current order state, if provided
 * @param {OrderFulfillment} [fulfillment] the current fulfillment
 * @returns {Object}
 */
export default function useContextParams(metadata, orderState, fulfillment) {
  const { id } = useParams();
  const tracking = useTracking(metadata);
  return useMemo(() => {
    let contextParams = {};
    if (id) {
      contextParams.id = id;
    }

    if (tracking) {
      contextParams[ContextParameters.TRACKING] = tracking;
    }

    if (fulfillment) {
      contextParams.parent = contextParams.fulfillment = fulfillment;
    }

    if (orderState && orderState.data) {
      contextParams.order = orderState.data;
      contextParams.orderId = orderState.data.id;
    }

    if (fulfillment) {
      contextParams.fulfillmentId = fulfillment.id;
    }

    return contextParams;
  }, [fulfillment, id, orderState, tracking]);
}
